if (!customElements.get('number-counter')) {
  class NumberCounter extends HTMLElement {
    constructor() {
      super();
      this.hasAnimated = false;
    }

    connectedCallback() {
      const init = () => {
        if (this.hasInitialized) return;
        this.hasInitialized = true;
        
        this.prepareNumbers();
        
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.hasAnimated) {
                this.hasAnimated = true;
                this.animateNumbers();
                this.observer.disconnect();
              }
            });
          },
          { threshold: 0 }
        );
        
        let target = this;
        const style = window.getComputedStyle(this);
        if (style.display === 'contents' && this.parentElement) {
          target = this.parentElement;
        }
        this.observer.observe(target);
      };

      // In some environments (like Shopify theme editor), connectedCallback 
      // can fire before the element's children are fully parsed.
      if (this.textContent.trim().length > 0) {
        init();
      } else {
        let attempts = 0;
        const checkChildren = () => {
          if (this.textContent.trim().length > 0) {
            init();
          } else if (attempts < 20) {
            attempts++;
            requestAnimationFrame(checkChildren);
          }
        };
        requestAnimationFrame(checkChildren);
      }
    }

    prepareNumbers() {
      // Find all text nodes that contain numbers and wrap them if not already wrapped
      const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, null, false);
      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue.trim() !== '' && /[0-9]/.test(node.nodeValue)) {
          textNodes.push(node);
        }
      }

      textNodes.forEach((textNode) => {
        const regex = /([0-9]+(?:[,.][0-9]+)*)/g;
        const text = textNode.nodeValue;
        if (!regex.test(text)) return;
        
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;
        
        regex.lastIndex = 0; // reset regex
        while ((match = regex.exec(text)) !== null) {
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
          }
          
          const rawMatch = match[0];
          const hasComma = rawMatch.includes(',');
          const hasDecimals = rawMatch.includes('.');
          const cleanTarget = parseFloat(rawMatch.replace(/,/g, ''));

          const span = document.createElement('span');
          span.className = 'number-counter-animating';
          span.dataset.target = cleanTarget;
          span.dataset.hasDecimals = hasDecimals;
          span.dataset.hasComma = hasComma;
          
          if (hasDecimals) {
            span.textContent = '0.0';
          } else {
            span.textContent = '0';
          }
          
          fragment.appendChild(span);
          
          lastIndex = regex.lastIndex;
        }
        
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }
        
        textNode.parentNode.replaceChild(fragment, textNode);
      });
    }

    animateNumbers() {
      const spans = this.querySelectorAll('.number-counter-animating');
      spans.forEach(span => {
        const target = parseFloat(span.dataset.target);
        const hasDecimals = span.dataset.hasDecimals === 'true';
        const hasComma = span.dataset.hasComma === 'true';
        this.startCounter(span, target, hasDecimals, hasComma);
      });
    }

    startCounter(element, target, hasDecimals, hasComma) {
      const duration = 2000; // 2 seconds
      let startTime = null;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        let progress = elapsed / duration;
        if (progress > 1) progress = 1;
        
        // Ease out quad
        const easeOut = progress * (2 - progress);
        const current = target * easeOut;
        
        if (hasDecimals) {
          element.textContent = current.toFixed(1);
        } else {
          let val = Math.round(current).toString();
          if (hasComma) {
            val = val.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          }
          element.textContent = val;
        }
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          if (hasDecimals) {
            element.textContent = target.toFixed(1);
          } else {
            let val = target.toString();
            if (hasComma) {
              val = val.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }
            element.textContent = val;
          }
        }
      };

      requestAnimationFrame(animate);
    }
  }

  customElements.define('number-counter', NumberCounter);
}
